import {
  faCircleCheck as faCircleCheckRegular,
  faLink as faLinkRegular,
  faPenNib as faPenNibRegular,
  faTableList as faTableListRegular,
  faText as faTextRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faCircleCheck as faCircleCheckSolid,
  faCircleInfo as faCircleInfoSolid,
  faLink as faLinkSolid,
  faPenNib as faPenNibSolid,
  faTableList as faTableListSolid,
  faText as faTextSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Text } from '@modusign/design-system';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useGNBFoldHandle } from '@app/containers/GlobalNavigationBar/GNBFoldHandleContext';
import GNBLinkButtonIcon from '@app/containers/GlobalNavigationBar/GNBLinkButtonIcon';
import GuideTooltip from '@app/containers/GlobalNavigationBar/GuideTooltip';
import { useSubscription } from '@app/features/subscription/hooks/subscription';
import useGNBGuideActive from '@app/hooks/useGNBGuideActive';
import { recordGTMEvent } from '@app/store/analytics/actions';
import { refetchEditingDocumentList } from '@app/store/editingDocuments/actions';

import GlobalNavigationBarMenu from '../GlobalNavigationBarMenu';
import ApprovalVideo from '../GuideTooltip/approval.mp4';
import BulkVideo from '../GuideTooltip/bulk.mp4';
import EditVideo from '../GuideTooltip/edit.mp4';
import LinkVideo from '../GuideTooltip/link.mp4';

function SignatureSection() {
  const dispatch = useDispatch();
  const isGuideActive = useGNBGuideActive();

  const handleEditingDocumentsLinkClick = useCallback(() => {
    dispatch(refetchEditingDocumentList());
  }, [dispatch]);

  const { data: subscription } = useSubscription();

  const { folded } = useGNBFoldHandle();

  const primaryBadges = useMemo(() => {
    // 요금제 개편일(2023-12-01) 이후부터는 트라이얼의 서명 요청이 30건으로 제한되도록 변경됨
    if (subscription?.createdAt && moment(subscription.createdAt).isAfter('2023-12-01')) {
      return ['서명 요청, 대량전송, 링크서명 포함 총 30건 제공'];
    }

    return undefined;
  }, [subscription?.createdAt]);

  return (
    <Box as="section">
      {folded || (
        <Text as="div" color="textNeutralUltraLight" fontSize="sm" fontWeight="bold" p={3} pb={1}>
          서명
        </Text>
      )}
      <Flex
        alignItems={folded ? 'center' : 'initial'}
        as="ul"
        flexDirection="column"
        gap={folded ? 4 : 0}
      >
        <GuideTooltip
          name="대량전송"
          primaryBadges={primaryBadges}
          title="여러 명에게 동일한 양식의 계약으로 일괄 서명 요청 보낼 수 있어요."
          video={BulkVideo}
        >
          <GlobalNavigationBarMenu
            leftIcon={<GNBLinkButtonIcon icon={faTableListRegular} />}
            leftIconActive={<GNBLinkButtonIcon icon={faTableListSolid} />}
            rightIcon={isGuideActive && <FontAwesomeIcon icon={faCircleInfoSolid} width="11px" />}
            rightIconHovered={
              isGuideActive && (
                <FontAwesomeIcon color="iconEmphasisBase" icon={faCircleInfoSolid} width="11px" />
              )
            }
            to="/bulk-requests"
          >
            대량전송
          </GlobalNavigationBarMenu>
        </GuideTooltip>
        <GuideTooltip
          name="링크서명"
          primaryBadges={primaryBadges}
          title="불특정 다수에게 문서의 URL 링크 또는 QR 코드를 공유하여 서명을 받을 수 있어요."
          video={LinkVideo}
        >
          <GlobalNavigationBarMenu
            isActive={(_, location) => location.pathname.startsWith('/link')}
            leftIcon={<GNBLinkButtonIcon icon={faLinkRegular} />}
            leftIconActive={<GNBLinkButtonIcon icon={faLinkSolid} />}
            rightIcon={isGuideActive && <FontAwesomeIcon icon={faCircleInfoSolid} width="11px" />}
            rightIconHovered={
              isGuideActive && (
                <FontAwesomeIcon color="iconEmphasisBase" icon={faCircleInfoSolid} width="11px" />
              )
            }
            to="/links"
          >
            링크서명
          </GlobalNavigationBarMenu>
        </GuideTooltip>
        <GuideTooltip
          name="내부결재"
          secondaryBadges={['*공용 워크스페이스 · 멤버 초대 필요']}
          title="서명 요청 전 지정된 구성원에게 내부결재 절차를 거쳐 결재를 받고, 서명자에게 서명을 요청할 수 있습니다."
          video={ApprovalVideo}
        >
          <GlobalNavigationBarMenu
            leftIcon={<GNBLinkButtonIcon icon={faCircleCheckRegular} />}
            leftIconActive={<GNBLinkButtonIcon icon={faCircleCheckSolid} />}
            rightIcon={isGuideActive && <FontAwesomeIcon icon={faCircleInfoSolid} width="11px" />}
            rightIconHovered={
              isGuideActive && (
                <FontAwesomeIcon color="iconEmphasisBase" icon={faCircleInfoSolid} width="11px" />
              )
            }
            style={{ position: 'relative' }}
            to="/approvals"
            onClick={() => {
              dispatch(recordGTMEvent('gnbApprovalClick'));
            }}
          >
            내부결재
          </GlobalNavigationBarMenu>
        </GuideTooltip>
        <GuideTooltip
          name="문서 편집기"
          title="가지고 있는 문서 파일에 텍스트, 사인·도장, 체크박스를 추가해서 새로 저장할 수 있어요."
          video={EditVideo}
        >
          <GlobalNavigationBarMenu
            leftIcon={<GNBLinkButtonIcon icon={faTextRegular} />}
            leftIconActive={<GNBLinkButtonIcon icon={faTextSolid} />}
            rightIcon={isGuideActive && <FontAwesomeIcon icon={faCircleInfoSolid} width="11px" />}
            rightIconHovered={
              isGuideActive && (
                <FontAwesomeIcon color="iconEmphasisBase" icon={faCircleInfoSolid} width="11px" />
              )
            }
            to="/editing-documents"
            onClick={handleEditingDocumentsLinkClick}
          >
            문서 편집기
          </GlobalNavigationBarMenu>
        </GuideTooltip>
        <GlobalNavigationBarMenu
          leftIcon={<GNBLinkButtonIcon icon={faPenNibRegular} />}
          leftIconActive={<GNBLinkButtonIcon icon={faPenNibSolid} />}
          to="/signatures"
        >
          사인 · 도장
        </GlobalNavigationBarMenu>
      </Flex>
    </Box>
  );
}

export default SignatureSection;
